import { useState, useRef, useMemo, useCallback } from 'react'
import type {
  HotelSearchResultsData,
  HotelSearchOffer,
  HotelMapViewData,
  ExperienceMapViewData,
  ExperienceSearchResultsData,
} from 'tripPlanner/api/types'
import {
  transformOffersToMarkers,
  transformOffersToExperienceMarkers,
  type MapMarkerComponent,
} from 'tripPlanner/utils/mapMarkerTransforms'

import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import { hotelSearchResultParams } from './hotelSearchResultParams'

type SearchStatus = 'idle' | 'success' | 'error'

interface HotelSearchState {
  searchParams?: OfferClickSearchParams
  status: SearchStatus
  searchQuery: string
  offers: Array<HotelSearchOffer>
  errorMessage?: string
}

interface ReplayDismissGuard {
  shouldIgnoreUpdate: (generation: number, callId?: string) => boolean
  markAccepted: (generation: number, callId?: string) => void
  markDismissed: () => void
  reset: () => void
  bumpGeneration: () => void
}

function useReplayDismissGuard(): ReplayDismissGuard {
  const generationRef = useRef(0)
  const dismissedGenerationRef = useRef<number | undefined>(undefined)
  const dismissedCallIdRef = useRef<string | undefined>(undefined)
  const activeCallIdRef = useRef<string | undefined>(undefined)

  const shouldIgnoreUpdate = useCallback((generation: number, callId?: string) => {
    if (callId && dismissedCallIdRef.current === callId) {
      return true
    }
    if (!callId && dismissedGenerationRef.current === generation) {
      return true
    }
    return generation < generationRef.current
  }, [])

  const markAccepted = useCallback((generation: number, callId?: string) => {
    generationRef.current = generation
    dismissedGenerationRef.current = undefined
    dismissedCallIdRef.current = undefined
    activeCallIdRef.current = callId
  }, [])

  const markDismissed = useCallback(() => {
    dismissedGenerationRef.current = generationRef.current
    dismissedCallIdRef.current = activeCallIdRef.current
  }, [])

  const reset = useCallback(() => {
    generationRef.current = 0
    dismissedGenerationRef.current = undefined
    dismissedCallIdRef.current = undefined
    activeCallIdRef.current = undefined
  }, [])

  const bumpGeneration = useCallback(() => {
    generationRef.current += 1
    dismissedGenerationRef.current = undefined
    dismissedCallIdRef.current = undefined
    activeCallIdRef.current = undefined
  }, [])

  return useMemo(() => ({
    shouldIgnoreUpdate,
    markAccepted,
    markDismissed,
    reset,
    bumpGeneration,
  }), [shouldIgnoreUpdate, markAccepted, markDismissed, reset, bumpGeneration])
}

interface UseSearchResultsReturn {
  /** Current search status */
  status: SearchStatus
  /** The NLP search query (used for carousel title and filter chip) */
  searchQuery: string
  /** The hotel offers from the search */
  offers: Array<HotelSearchOffer>
  /** Error message if search failed */
  errorMessage?: string
  /** Computed map markers from offers (memoized) */
  hotelMapMarkers: Array<MapMarkerComponent>
  /** Whether hotel filter is currently visible */
  isHotelFilterVisible: boolean
  /** Whether hotel search markers should be active on the map */
  isHotelSearchActive: boolean
  /** Set success state with results (called when results arrive) */
  setHotelSearchResults: (data: HotelSearchResultsData, generation: number) => boolean
  /** Set error state (called when search fails) */
  setHotelSearchError: (message: string, generation: number, hotelSearchCallId?: string) => boolean
  /** Populate state from carousel click (for historical carousels) */
  setHotelSearchResultsFromCarousel: (data: HotelMapViewData) => void
  /** Hide the hotel filter (called when filter chip is dismissed) */
  hideHotelFilter: () => void
  /** Reset hotel search state (called when starting a new chat) */
  resetHotelSearch: () => void
  /** The experience search query (used for filter chip label) */
  experienceSearchQuery: string
  /** Computed map markers from experience offers (memoized) */
  experienceMapMarkers: Array<MapMarkerComponent>
  /** Whether experience filter is currently visible */
  isExperienceFilterVisible: boolean
  /** Whether experience search markers should be active on the map */
  isExperienceSearchActive: boolean
  /** Populate experience state from carousel "Map view" click */
  setExperienceSearchResults: (data: ExperienceSearchResultsData, generation: number) => boolean
  setExperienceSearchError: (message: string, generation: number, searchCallId?: string) => boolean
  setExperienceSearchResultsFromCarousel: (data: ExperienceMapViewData) => void
  /** Hide the experience filter (called when filter chip is dismissed) */
  hideExperienceFilter: () => void
  /** Reset experience search state (called when starting a new chat) */
  resetExperienceSearch: () => void
}

/**
 * Hook to manage hotel search results state.
 * Handles success and error states with race condition protection.
 */
export function useSearchResults(): UseSearchResultsReturn {
  const [state, setState] = useState<HotelSearchState>({
    status: 'idle',
    searchQuery: '',
    offers: [],
  })

  const [isHotelFilterVisible, setIsHotelFilterVisible] = useState(false)
  const [isHotelSearchActive, setIsHotelSearchActive] = useState(false)
  const hotelReplayGuard = useReplayDismissGuard()

  const [experienceState, setExperienceState] = useState<HotelSearchState>({
    status: 'idle',
    searchQuery: '',
    offers: [],
  })

  const [isExperienceFilterVisible, setIsExperienceFilterVisible] = useState(false)
  const [isExperienceSearchActive, setIsExperienceSearchActive] = useState(false)
  const experienceReplayGuard = useReplayDismissGuard()

  const setHotelSearchResults = useCallback((
    data: HotelSearchResultsData,
    generation: number,
  ) => {
    if (hotelReplayGuard.shouldIgnoreUpdate(generation, data.hotelSearchCallId)) {
      return false
    }
    hotelReplayGuard.markAccepted(generation, data.hotelSearchCallId)
    setState({
      status: 'success',
      searchQuery: data.searchQuery,
      offers: data.offers,
      searchParams: hotelSearchResultParams(data),
    })
    setIsHotelSearchActive(data.offers.length > 0)
    setIsHotelFilterVisible(false)
    return true
  }, [hotelReplayGuard])

  const setHotelSearchError = useCallback((message: string, generation: number, hotelSearchCallId?: string) => {
    if (hotelReplayGuard.shouldIgnoreUpdate(generation, hotelSearchCallId)) return false
    hotelReplayGuard.markAccepted(generation, hotelSearchCallId)
    setState(prev => ({
      ...prev,
      status: 'error',
      errorMessage: message,
    }))
    setIsHotelSearchActive(prev => prev || state.offers.length > 0)
    // Don't hide hotel filter — preserve previous results if any exist
    return true
  }, [hotelReplayGuard, state.offers.length])

  const setHotelSearchResultsFromCarousel = useCallback((
    data: HotelMapViewData,
  ) => {
    hotelReplayGuard.bumpGeneration()
    setState({
      status: 'success',
      searchQuery: data.searchQuery,
      offers: data.offers,
      searchParams: data.searchParams,
    })
    setIsHotelSearchActive(data.offers.length > 0)
    setIsHotelFilterVisible(true)
  }, [hotelReplayGuard])

  const hideHotelFilter = useCallback(() => {
    setIsHotelSearchActive(false)
    setIsHotelFilterVisible(false)
    hotelReplayGuard.markDismissed()
  }, [hotelReplayGuard])

  const resetHotelSearch = useCallback(() => {
    setState({
      status: 'idle',
      searchQuery: '',
      offers: [],
    })
    setIsHotelSearchActive(false)
    setIsHotelFilterVisible(false)
    hotelReplayGuard.reset()
  }, [hotelReplayGuard])

  const hotelMapMarkers = useMemo(() => {
    if (state.status === 'idle' || state.offers.length === 0) {
      return []
    }
    // Show markers for both 'success' and 'error' (preserves previous results on error)
    return transformOffersToMarkers(state.offers, state.searchParams)
  }, [state.status, state.offers, state.searchParams])

  const setExperienceSearchResults = useCallback((data: ExperienceSearchResultsData, generation: number) => {
    if (experienceReplayGuard.shouldIgnoreUpdate(generation, data.searchCallId)) {
      return false
    }
    experienceReplayGuard.markAccepted(generation, data.searchCallId)
    setExperienceState({
      status: 'success',
      searchQuery: data.searchQuery,
      offers: data.offers,
    })
    setIsExperienceSearchActive(data.offers.length > 0)
    setIsExperienceFilterVisible(false)
    return true
  }, [experienceReplayGuard])

  const setExperienceSearchError = useCallback((
    message: string,
    generation: number,
    searchCallId?: string,
  ) => {
    if (experienceReplayGuard.shouldIgnoreUpdate(generation, searchCallId)) return false
    experienceReplayGuard.markAccepted(generation, searchCallId)
    setExperienceState(prev => ({
      ...prev,
      status: 'error',
      errorMessage: message,
    }))
    setIsExperienceSearchActive(prev => prev || experienceState.offers.length > 0)
    return true
  }, [experienceState.offers.length, experienceReplayGuard])

  const setExperienceSearchResultsFromCarousel = useCallback((data: ExperienceMapViewData) => {
    experienceReplayGuard.bumpGeneration()
    setExperienceState({
      status: 'success',
      searchQuery: data.searchQuery,
      offers: data.offers,
    })
    setIsExperienceSearchActive(data.offers.length > 0)
    setIsExperienceFilterVisible(true)
  }, [experienceReplayGuard])

  const hideExperienceFilter = useCallback(() => {
    setIsExperienceSearchActive(false)
    setIsExperienceFilterVisible(false)
    experienceReplayGuard.markDismissed()
  }, [experienceReplayGuard])

  const resetExperienceSearch = useCallback(() => {
    setExperienceState({
      status: 'idle',
      searchQuery: '',
      offers: [],
    })
    setIsExperienceSearchActive(false)
    setIsExperienceFilterVisible(false)
    experienceReplayGuard.reset()
  }, [experienceReplayGuard])

  const experienceMapMarkers = useMemo(() => {
    if (experienceState.status === 'idle' || experienceState.offers.length === 0) {
      return []
    }
    return transformOffersToExperienceMarkers(experienceState.offers)
  }, [experienceState.status, experienceState.offers])

  return {
    status: state.status,
    searchQuery: state.searchQuery,
    offers: state.offers,
    errorMessage: state.errorMessage,
    hotelMapMarkers,
    isHotelFilterVisible,
    isHotelSearchActive,
    setHotelSearchResults,
    setHotelSearchError,
    setHotelSearchResultsFromCarousel,
    hideHotelFilter,
    resetHotelSearch,
    experienceSearchQuery: experienceState.searchQuery,
    experienceMapMarkers,
    isExperienceFilterVisible,
    isExperienceSearchActive,
    setExperienceSearchResults,
    setExperienceSearchError,
    setExperienceSearchResultsFromCarousel,
    hideExperienceFilter,
    resetExperienceSearch,
  }
}
